// role based report

import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Header,
  Segment,
  Table,
  Label,
  Icon,
  Button,
  Dropdown,
  Statistic,
  Message,
  Divider,
} from "semantic-ui-react";
import { invenioConfig } from "@config";

class RoleBasedBorrowingReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedRole: "",
      selectedProgram: [],
      selectedYearLevel: [],
      sortBy: "totalLoans",
      sortOrder: "desc",
      isGenerating: false,
      availablePrograms: [],
      availableYearLevels: [],
      loadingFilters: false,
    };
  }

  static propTypes = {
    data: PropTypes.object.isRequired,
  };

  componentDidMount() {
    this.fetchStudentFilters();
  }

  fetchStudentFilters = async () => {
    try {
      this.setState({ loadingFilters: true });
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;

      const [programsRes, yearLevelsRes] = await Promise.all([
        fetch(`${baseURL}/document-usage/programs`),
        fetch(`${baseURL}/document-usage/year-levels`),
      ]);

      if (programsRes.ok) {
        const programsResult = await programsRes.json();
        if (programsResult.status === "success") {
          this.setState({ availablePrograms: programsResult.data });
        }
      }

      if (yearLevelsRes.ok) {
        const yearLevelsResult = await yearLevelsRes.json();
        if (yearLevelsResult.status === "success") {
          this.setState({ availableYearLevels: yearLevelsResult.data });
        }
      }
    } catch (error) {
      console.error("Error fetching student filters:", error);
    } finally {
      this.setState({ loadingFilters: false });
    }
  };

  toSentenceCase = (text) => {
    if (!text || typeof text !== "string") return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  analyzeByRole = () => {
    const { data } = this.props;
    const { selectedRole, selectedProgram, selectedYearLevel } = this.state;
    const roleAnalysis = {};
    let totalLoans = 0;

    if (!data || !data.hits) return { roleAnalysis: {}, totals: {} };

    const globalUniqueBooks = new Set();
    const globalUniquePatrons = new Set();

    data.hits.forEach((hit) => {
      const {
        title,
        authors = [],
        pid,
        patron_info: patronInfo = {},
      } = hit.metadata;

      globalUniqueBooks.add(pid);

      const allPatrons = [];
      if (patronInfo.most_frequent_patron) {
        allPatrons.push(patronInfo.most_frequent_patron);
      }

      if (patronInfo.top_patrons && patronInfo.top_patrons.length > 0) {
        const existingPids = new Set(
          [patronInfo.most_frequent_patron?.pid].filter(Boolean)
        );
        patronInfo.top_patrons.forEach((patron) => {
          if (!existingPids.has(patron.pid)) {
            allPatrons.push(patron);
            existingPids.add(patron.pid);
          }
        });
      }

      allPatrons.forEach((patron) => {
        const role = this.getPatronRole(patron);
        const patronLoans = patron.loan_count || 0;

        // Apply role filter
        if (selectedRole && role !== selectedRole) {
          return;
        }

        // Apply student-specific filters ONLY when Student role is selected
        if (selectedRole === "student" && role === "student") {
          // Apply program filter for students (multiple selection)
          if (selectedProgram && selectedProgram.length > 0) {
            const matchesAnyProgram = selectedProgram.some((prog) =>
              this.matchesProgram(patron, prog)
            );
            if (!matchesAnyProgram) {
              return;
            }
          }

          // Apply year level filter for students (multiple selection)
          if (selectedYearLevel && selectedYearLevel.length > 0) {
            const matchesAnyYearLevel = selectedYearLevel.some((year) =>
              this.matchesYearLevel(patron, year)
            );
            if (!matchesAnyYearLevel) {
              return;
            }
          }
        }

        totalLoans += patronLoans;
        globalUniquePatrons.add(patron.pid);

        if (!roleAnalysis[role]) {
          roleAnalysis[role] = {
            role: role,
            totalLoans: 0,
            uniqueBooks: new Set(),
            uniquePatrons: new Set(),
            books: {},
            patrons: {},
            studentDemographics: {},
          };
        }

        roleAnalysis[role].totalLoans += patronLoans;
        roleAnalysis[role].uniqueBooks.add(pid);
        roleAnalysis[role].uniquePatrons.add(patron.pid);

        // Track detailed student demographics ONLY for students
        if (role === "student") {
          const program = patron.program || "Unknown Program";
          const yearLevel = patron.year_level || "Unknown Year";
          const demographicKey = `${program}|${yearLevel}`;

          if (!roleAnalysis[role].studentDemographics[demographicKey]) {
            roleAnalysis[role].studentDemographics[demographicKey] = {
              program: program,
              yearLevel: yearLevel,
              count: 0,
              totalLoans: 0,
              patrons: new Set(),
            };
          }

          roleAnalysis[role].studentDemographics[demographicKey].count += 1;
          roleAnalysis[role].studentDemographics[demographicKey].totalLoans +=
            patronLoans;
          roleAnalysis[role].studentDemographics[demographicKey].patrons.add(
            patron.pid
          );
        }

        if (!roleAnalysis[role].books[pid]) {
          roleAnalysis[role].books[pid] = {
            title,
            authors: authors.map((a) => a.full_name || a.name).filter(Boolean),
            totalLoans: 0,
            uniquePatrons: new Set(),
          };
        }
        roleAnalysis[role].books[pid].totalLoans += patronLoans;
        roleAnalysis[role].books[pid].uniquePatrons.add(patron.pid);

        if (!roleAnalysis[role].patrons[patron.pid]) {
          roleAnalysis[role].patrons[patron.pid] = {
            name: patron.name || "Unknown",
            email: patron.email || "",
            profileId: patron.profile_id || "",
            program: patron.program || "",
            yearLevel: patron.year_level || "",
            department: patron.department || "",
            totalLoans: 0,
            uniqueBooks: new Set(),
          };
        }
        roleAnalysis[role].patrons[patron.pid].totalLoans += patronLoans;
        roleAnalysis[role].patrons[patron.pid].uniqueBooks.add(pid);
      });
    });

    Object.keys(roleAnalysis).forEach((role) => {
      const analysis = roleAnalysis[role];
      analysis.uniqueBooksCount = analysis.uniqueBooks.size;
      analysis.uniquePatronsCount = analysis.uniquePatrons.size;

      if (role === "student") {
        Object.keys(analysis.studentDemographics).forEach((demographicKey) => {
          const demographic = analysis.studentDemographics[demographicKey];
          demographic.uniquePatronsCount = demographic.patrons.size;
          delete demographic.patrons;
        });
      }

      delete analysis.uniqueBooks;
      delete analysis.uniquePatrons;
    });

    return {
      roleAnalysis,
      totals: {
        totalUniqueBooks: globalUniqueBooks.size,
        totalLoans: totalLoans,
        totalUniquePatrons: globalUniquePatrons.size,
        totalRoles: Object.keys(roleAnalysis).length,
      },
    };
  };

  matchesProgram = (patron, selectedProgram) => {
    const patronProgram = patron.program || "";
    if (!patronProgram) return false;

    const { availablePrograms } = this.state;
    const programOption = availablePrograms.find(
      (p) => p.value === selectedProgram
    );

    if (programOption) {
      const directMatch = patronProgram === programOption.db_value;
      const caseInsensitiveMatch =
        patronProgram.toLowerCase() ===
        (programOption.db_value || "").toLowerCase();

      return directMatch || caseInsensitiveMatch;
    }

    return patronProgram.toLowerCase() === selectedProgram.toLowerCase();
  };

  matchesYearLevel = (patron, selectedYearLevel) => {
    const patronYearLevel = patron.year_level || patron.year || "";
    if (!patronYearLevel) return false;

    const { availableYearLevels } = this.state;
    const yearLevelOption = availableYearLevels.find(
      (y) => y.value === selectedYearLevel
    );

    if (yearLevelOption) {
      return (
        patronYearLevel === yearLevelOption.db_value ||
        patronYearLevel === yearLevelOption.value ||
        patronYearLevel.toString() === yearLevelOption.db_value ||
        patronYearLevel.toString() === yearLevelOption.value
      );
    }

    const normalizedPatron = patronYearLevel.toString().toLowerCase().trim();
    const normalizedSelected = selectedYearLevel.toLowerCase().trim();

    if (normalizedPatron === normalizedSelected) return true;

    const yearMappings = {
      1: ["first", "first_year", "1st", "1st_year", "first year"],
      2: ["second", "second_year", "2nd", "2nd_year", "second year"],
      3: ["third", "third_year", "3rd", "3rd_year", "third year"],
      4: ["fourth", "fourth_year", "4th", "4th_year", "fourth year"],
      5: ["fifth", "fifth_year", "5th", "5th_year", "fifth year"],
    };

    for (const [key, variants] of Object.entries(yearMappings)) {
      if (
        variants.includes(normalizedPatron) &&
        variants.includes(normalizedSelected)
      ) {
        return true;
      }
      if (normalizedPatron === key && variants.includes(normalizedSelected)) {
        return true;
      }
      if (variants.includes(normalizedPatron) && normalizedSelected === key) {
        return true;
      }
    }

    return false;
  };

  getPatronRole = (patron) => {
    const role = patron.patron_type || patron.role || patron.user_type;
    return role && role.trim() !== "" ? role.toLowerCase() : "student";
  };

  handleRoleChange = (event, { value }) => {
    this.setState({
      selectedRole: value,
      selectedProgram: value !== "student" ? [] : this.state.selectedProgram,
      selectedYearLevel:
        value !== "student" ? [] : this.state.selectedYearLevel,
    });
  };

  handleProgramChange = (event, { value }) => {
    this.setState({ selectedProgram: value });
  };

  handleYearLevelChange = (event, { value }) => {
    this.setState({ selectedYearLevel: value });
  };

  handleSortChange = (event, { value }) => {
    this.setState({ sortBy: value });
  };

  handleSortOrderToggle = () => {
    this.setState((prevState) => ({
      sortOrder: prevState.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  getFilteredAndSortedRoles = (roleAnalysis) => {
    const { selectedRole, sortBy, sortOrder } = this.state;
    let roles = Object.values(roleAnalysis);

    if (selectedRole) {
      roles = roles.filter((role) => role.role === selectedRole);
    }

    roles.sort((a, b) => {
      let valueA, valueB;
      switch (sortBy) {
        case "totalLoans":
          valueA = a.totalLoans;
          valueB = b.totalLoans;
          break;
        case "uniqueBooks":
          valueA = a.uniqueBooksCount;
          valueB = b.uniqueBooksCount;
          break;
        case "uniquePatrons":
          valueA = a.uniquePatronsCount;
          valueB = b.uniquePatronsCount;
          break;
        default:
          valueA = a.totalLoans;
          valueB = b.totalLoans;
      }
      return sortOrder === "desc" ? valueB - valueA : valueA - valueB;
    });

    return roles;
  };

  // Enhanced student demographics rendering with separate columns
  renderStudentDemographics = (role) => {
    const { selectedRole } = this.state;

    if (
      role.role !== "student" ||
      selectedRole !== "student" ||
      !role.studentDemographics ||
      Object.keys(role.studentDemographics).length === 0
    ) {
      return null;
    }

    const demographics = Object.values(role.studentDemographics).sort(
      (a, b) => b.uniquePatronsCount - a.uniquePatronsCount
    );

    return (
      <Table basic="very" compact size="small" style={{ margin: 0 }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ fontWeight: "bold" }}>
              Program
            </Table.HeaderCell>
            <Table.HeaderCell style={{ fontWeight: "bold" }}>
              Year
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" style={{ fontWeight: "bold" }}>
              Patrons
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center" style={{ fontWeight: "bold" }}>
              Loans
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {demographics.map((data, index) => (
            <Table.Row key={index}>
              <Table.Cell style={{ maxWidth: "120px" }}>
                <strong>{data.program}</strong>
              </Table.Cell>
              <Table.Cell>
                <strong>{data.yearLevel}</strong>
              </Table.Cell>
              <Table.Cell textAlign="center">
                <strong>{data.uniquePatronsCount}</strong>
              </Table.Cell>
              <Table.Cell textAlign="center">
                <strong>{data.totalLoans}</strong>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  // Updated table headers based on whether we're showing demographics
  generateTableHeaders = () => {
    const { selectedRole } = this.state;
    const showDemographics = selectedRole === "student";

    if (showDemographics) {
      return `
        <th style="width: 8%;">Rank</th>
        <th style="width: 15%;">User Role</th>
        <th style="width: 12%;">Total Loans</th>
        <th style="width: 12%;">Loaned Books</th>
        <th style="width: 10%;">Patrons</th>
        <th style="width: 43%;">Student Demographics</th>
      `;
    } else {
      return `
        <th style="width: 15%;">Rank</th>
        <th style="width: 25%;">User Role</th>
        <th style="width: 20%;">Total Loans</th>
        <th style="width: 20%;">Loaned Books</th>
        <th style="width: 20%;">Patrons</th>
      `;
    }
  };

  generateTableRows = (roles) => {
    const { selectedRole } = this.state;
    const showDemographics = selectedRole === "student";

    return roles
      .map((role, index) => {
        let demographicsText = "";

        if (
          showDemographics &&
          role.role === "student" &&
          role.studentDemographics
        ) {
          const demographics = Object.values(role.studentDemographics)
            .sort((a, b) => b.uniquePatronsCount - a.uniquePatronsCount)
            .map(
              (data) =>
                `${data.program} - ${data.yearLevel}: ${data.uniquePatronsCount} patrons, ${data.totalLoans} loans`
            )
            .join("<br/>");
          demographicsText = demographics || "No demographic data";
        }

        if (showDemographics) {
          return `
          <tr>
            <td style="text-align: center;">${index + 1}</td>
            <td><strong>${this.escapeHtml(
              this.toSentenceCase(role.role)
            )}</strong></td>
            <td style="text-align: center;"><strong>${
              role.totalLoans
            }</strong></td>
            <td style="text-align: center;">${role.uniqueBooksCount}</td>
            <td style="text-align: center;">${role.uniquePatronsCount}</td>
            <td style="font-size: 11px; line-height: 1.3;">${demographicsText}</td>
          </tr>
          `;
        } else {
          return `
          <tr>
            <td style="text-align: center;">${index + 1}</td>
            <td><strong>${this.escapeHtml(
              this.toSentenceCase(role.role)
            )}</strong></td>
            <td style="text-align: center;"><strong>${
              role.totalLoans
            }</strong></td>
            <td style="text-align: center;">${role.uniqueBooksCount}</td>
            <td style="text-align: center;">${role.uniquePatronsCount}</td>
          </tr>
          `;
        }
      })
      .join("");
  };

  exportToCSV = () => {
    const { roleAnalysis } = this.analyzeByRole();
    const roles = this.getFilteredAndSortedRoles(roleAnalysis);
    const { selectedRole } = this.state;
    const showDemographics = selectedRole === "student";

    let summaryHeaders, summaryRows;

    if (showDemographics) {
      summaryHeaders = [
        "Rank",
        "User Role",
        "Total Loans",
        "Loaned Books",
        "Patrons",
        "Program",
        "Year Level",
        "Students per Program",
        "Loans per Program",
      ];

      summaryRows = [];
      roles.forEach((role, index) => {
        if (role.role === "student" && role.studentDemographics) {
          const demographics = Object.values(role.studentDemographics).sort(
            (a, b) => b.uniquePatronsCount - a.uniquePatronsCount
          );
          if (demographics.length > 0) {
            demographics.forEach((demo, demoIndex) => {
              summaryRows.push([
                demoIndex === 0 ? index + 1 : "",
                demoIndex === 0 ? `"${role.role}"` : "",
                demoIndex === 0 ? role.totalLoans : "",
                demoIndex === 0 ? role.uniqueBooksCount : "",
                demoIndex === 0 ? role.uniquePatronsCount : "",
                `"${demo.program}"`,
                `"${demo.yearLevel}"`,
                demo.uniquePatronsCount,
                demo.totalLoans,
              ]);
            });
          } else {
            summaryRows.push([
              index + 1,
              `"${role.role}"`,
              role.totalLoans,
              role.uniqueBooksCount,
              role.uniquePatronsCount,
              "N/A",
              "N/A",
              "N/A",
              "N/A",
            ]);
          }
        } else {
          summaryRows.push([
            index + 1,
            `"${role.role}"`,
            role.totalLoans,
            role.uniqueBooksCount,
            role.uniquePatronsCount,
            "N/A",
            "N/A",
            "N/A",
            "N/A",
          ]);
        }
      });
    } else {
      summaryHeaders = [
        "Rank",
        "User Role",
        "Total Loans",
        "Loaned Books",
        "Patrons",
      ];

      summaryRows = roles.map((role, index) => [
        index + 1,
        `"${role.role}"`,
        role.totalLoans,
        role.uniqueBooksCount,
        role.uniquePatronsCount,
      ]);
    }

    const summaryCsv = [
      summaryHeaders.join(","),
      ...summaryRows.map((row) => row.join(",")),
    ].join("\n");

    this.downloadCSV(summaryCsv, "role_based_borrowing_report.csv");
  };

  exportToPDF = () => {
    const { roleAnalysis } = this.analyzeByRole();
    const roles = this.getFilteredAndSortedRoles(roleAnalysis);
    const {
      selectedRole,
      selectedProgram,
      selectedYearLevel,
      availablePrograms,
      availableYearLevels,
    } = this.state;

    const baseUrl = window.location.origin;
    const showDemographics = selectedRole === "student";

    let reportTitle = "Library Borrowing Analysis by User Role";
    if (selectedRole) {
      reportTitle += ` - ${this.toSentenceCase(selectedRole)}`;

      if (selectedRole === "student") {
        reportTitle += " with Student Demographics";
        const filters = [];
        if (selectedProgram) {
          const programName =
            availablePrograms.find((p) => p.value === selectedProgram)?.name ||
            selectedProgram;
          filters.push(`Program: ${programName}`);
        }
        if (selectedYearLevel) {
          const yearLevelName =
            availableYearLevels.find((y) => y.value === selectedYearLevel)
              ?.name || selectedYearLevel;
          filters.push(`Year: ${yearLevelName}`);
        }
        if (filters.length > 0) {
          reportTitle += ` (${filters.join(", ")})`;
        }
      }
    }

    // Generate table content based on demographics visibility
    let tableHeaders, tableRows;

    if (showDemographics) {
      tableHeaders = `
        <th style="width: 5%;">Rank</th>
        <th style="width: 10%;">User Role</th>
        <th style="width: 10%;">Total Loans</th>
        <th style="width: 10%;">Loaned Books</th>
        <th style="width: 10%;">Patrons</th>
        <th style="width: 20%;">Program</th>
        <th style="width: 13%;">Year Level</th>
        <th style="width: 10%;">Students per Program</th>
        <th style="width: 10%;">Loans per Program</th>
      `;

      tableRows = roles
        .map((role, index) => {
          if (role.role === "student" && role.studentDemographics) {
            const demographics = Object.values(role.studentDemographics).sort(
              (a, b) => b.uniquePatronsCount - a.uniquePatronsCount
            );

            if (demographics.length > 0) {
              return demographics
                .map(
                  (demo, demoIndex) => `
                  <tr>
                    <td style="text-align: center;">${
                      demoIndex === 0 ? index + 1 : ""
                    }</td>
                    <td><strong>${
                      demoIndex === 0
                        ? this.escapeHtml(this.toSentenceCase(role.role))
                        : ""
                    }</strong></td>
                    <td style="text-align: center;"><strong>${
                      demoIndex === 0 ? role.totalLoans : ""
                    }</strong></td>
                    <td style="text-align: center;">${
                      demoIndex === 0 ? role.uniqueBooksCount : ""
                    }</td>
                    <td style="text-align: center;">${
                      demoIndex === 0 ? role.uniquePatronsCount : ""
                    }</td>
                    <td style="font-size: 10px;">${this.escapeHtml(
                      demo.program
                    )}</td>
                    <td style="font-size: 10px;", "text-align: center">${this.escapeHtml(
                      demo.yearLevel
                    )}</td>
                    <td style="text-align: center; font-size: 10px;">${
                      demo.uniquePatronsCount
                    }</td>
                    <td style="text-align: center; font-size: 10px;">${
                      demo.totalLoans
                    }</td>
                  </tr>
                `
                )
                .join("");
            } else {
              return `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td><strong>${this.escapeHtml(
                    this.toSentenceCase(role.role)
                  )}</strong></td>
                  <td style="text-align: center;"><strong>${
                    role.totalLoans
                  }</strong></td>
                  <td style="text-align: center;">${role.uniqueBooksCount}</td>
                  <td style="text-align: center;">${
                    role.uniquePatronsCount
                  }</td>
                  <td style="font-size: 10px;">N/A</td>
                  <td style="font-size: 10px;">N/A</td>
                  <td style="text-align: center; font-size: 10px;">N/A</td>
                  <td style="text-align: center; font-size: 10px;">N/A</td>
                </tr>
              `;
            }
          } else {
            return `
              <tr>
                <td style="text-align: center;">${index + 1}</td>
                <td><strong>${this.escapeHtml(
                  this.toSentenceCase(role.role)
                )}</strong></td>
                <td style="text-align: center;"><strong>${
                  role.totalLoans
                }</strong></td>
                <td style="text-align: center;">${role.uniqueBooksCount}</td>
                <td style="text-align: center;">${role.uniquePatronsCount}</td>
                <td style="font-size: 10px;">N/A</td>
                <td style="font-size: 10px;">N/A</td>
                <td style="text-align: center; font-size: 10px;">N/A</td>
                <td style="text-align: center; font-size: 10px;">N/A</td>
              </tr>
            `;
          }
        })
        .join("");
    } else {
      tableHeaders = `
        <th style="width: 15%;">Rank</th>
        <th style="width: 25%;">User Role</th>
        <th style="width: 20%;">Total Loans</th>
        <th style="width: 20%;">Loaned Books</th>
        <th style="width: 20%;">Patrons</th>
      `;

      tableRows = roles
        .map(
          (role, index) => `
          <tr>
            <td style="text-align: center;">${index + 1}</td>
            <td><strong>${this.escapeHtml(
              this.toSentenceCase(role.role)
            )}</strong></td>
            <td style="text-align: center;"><strong>${
              role.totalLoans
            }</strong></td>
            <td style="text-align: center;">${role.uniqueBooksCount}</td>
            <td style="text-align: center;">${role.uniquePatronsCount}</td>
          </tr>
        `
        )
        .join("");
    }

    const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${reportTitle}</title>
        <style>
          @media print { 
            body { margin: 0; } 
            .no-print { display: none; }
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            color: #333; 
            line-height: 1.4; 
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
          }
          .header h1 { 
            margin: 0 0 10px 0; 
            color: rgb(0, 0, 0); 
            font-size: 20px;
          }
          .logo-container {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 20px;
            gap: 20px;
          }
          .logo {
            max-height: 80px;
            max-width: 120px;
            object-fit: contain;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 10px; 
            font-size: ${showDemographics ? "9px" : "11px"};
          }
          th { 
            background-color: #225745; 
            color: white; 
            padding: ${showDemographics ? "6px 2px" : "8px 4px"}; 
            border: 1px solid #ddd; 
            text-align: center;
          }
          td { 
            padding: ${showDemographics ? "4px 2px" : "6px 4px"}; 
            border: 1px solid #ddd; 
            vertical-align: top;
          }
          tr:nth-child(even) { 
            background-color: #f9f9f9; 
          }
          .print-button { 
            background-color: #3498db; 
            color: white; 
            padding: 10px 20px; 
            border: none; 
            border-radius: 5px; 
            cursor: pointer; 
            margin-bottom: 20px; 
          }
        </style>
      </head>
      <body>
        <div class="no-print">
          <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
        </div>
        
        <div class="header">
          <div class="logo-container">
            <img src="${baseUrl}/images/university-logo.png" alt="University Logo" class="logo" onerror="this.style.display='none';">
            <img src="${baseUrl}/images/lrc-logo.png" alt="LRC Logo" class="logo" onerror="this.style.display='none';">
          </div>
          
          <h1>${reportTitle}</h1>
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
  
        <h2 style="color: rgb(0, 0, 0); border-bottom: 2px solid #3498db; padding-bottom: 10px; font-size: 16px;">
          Summary by User Role${
            showDemographics ? " with Student Demographics" : ""
          }
        </h2>
        <table>
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
  
        <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 15px;">
          <p>This report analyzes library borrowing patterns by user role${
            showDemographics ? " with detailed student demographics" : ""
          }.</p>
          <p>Generated by Library Management System</p>
        </div>
      </body>
    </html>
  `;

    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    if (printWindow) {
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.focus();
        }, 500);
      };
    }
  };

  downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  escapeHtml = (text) => {
    const div = document.createElement("div");
    div.textContent = text || "";
    return div.innerHTML;
  };

  render() {
    const { data } = this.props;
    const {
      selectedRole,
      selectedProgram,
      selectedYearLevel,
      sortBy,
      sortOrder,
      isGenerating,
      availablePrograms,
      availableYearLevels,
      loadingFilters,
    } = this.state;

    if (!data || !data.hits || data.hits.length === 0) {
      return (
        <Message warning>
          <Message.Header>No Data Available</Message.Header>
          <p>
            Please ensure you have document circulation data loaded to generate
            role-based borrowing reports.
          </p>
        </Message>
      );
    }

    const { roleAnalysis, totals } = this.analyzeByRole();
    const filteredRoles = this.getFilteredAndSortedRoles(roleAnalysis);

    const roleOptions = [
      { key: "all", text: "All Roles", value: "" },
      ...Object.keys(roleAnalysis).map((role) => ({
        key: role,
        text: this.toSentenceCase(role),
        value: role,
      })),
    ];

    const sortOptions = [
      { key: "totalLoans", text: "Total Loans", value: "totalLoans" },
      { key: "uniqueBooks", text: "Loaned Books", value: "uniqueBooks" },
      { key: "uniquePatrons", text: "Patrons", value: "uniquePatrons" },
    ];

    const programOptions = [
      // { key: 'all-programs', text: 'All Programs', value: '' },
      ...availablePrograms.map((program) => ({
        key: program.value,
        text: program.name,
        value: program.value,
      })),
    ];

    const yearLevelOptions = [
      // { key: 'all-years', text: 'All Year Levels', value: '' },
      ...availableYearLevels.map((year) => ({
        key: year.value,
        text: year.name,
        value: year.value,
      })),
    ];

    const showStudentFilters = selectedRole === "student";
    const showDemographicsColumn = selectedRole === "student";

    return (
      <div>
        <Header as="h3" color="green">
          <Header.Content style={{ marginTop: "20px" }}>
            Borrowing Analysis by User Role
            <Header.Subheader
              style={{
                color: "black",
                marginTop: "10px",
                marginBottom: "30px",
              }}
            >
              Comprehensive report showing library usage patterns across
              different user roles
            </Header.Subheader>
          </Header.Content>
        </Header>

        {/* Controls */}
        <Segment>
          <Grid stackable>
            <Grid.Row columns={showStudentFilters ? 5 : 3}>
              <Grid.Column width={showStudentFilters ? 3 : 5}>
                <Header as="h4">Filter by User Role</Header>
                <Dropdown
                  placeholder="Select a role"
                  fluid
                  selection
                  options={roleOptions}
                  value={selectedRole}
                  onChange={this.handleRoleChange}
                />
              </Grid.Column>

              {showStudentFilters && (
                <>
                  <Grid.Column width={3}>
                    <Header as="h4">Program</Header>
                    <Dropdown
                      placeholder="Select program(s)"
                      fluid
                      selection
                      multiple
                      options={programOptions}
                      value={selectedProgram}
                      onChange={this.handleProgramChange}
                      loading={loadingFilters}
                      disabled={loadingFilters}
                      clearable
                      search
                    />
                  </Grid.Column>

                  <Grid.Column width={3}>
                    <Header as="h4">Year Level </Header>
                    <Dropdown
                      placeholder="Select year level(s)"
                      fluid
                      selection
                      multiple
                      options={yearLevelOptions}
                      value={selectedYearLevel}
                      onChange={this.handleYearLevelChange}
                      loading={loadingFilters}
                      disabled={loadingFilters}
                      clearable
                      search
                    />
                  </Grid.Column>
                </>
              )}

              <Grid.Column width={showStudentFilters ? 3 : 5}>
                <Header as="h4">Sort By</Header>
                <Dropdown
                  fluid
                  selection
                  options={sortOptions}
                  value={sortBy}
                  onChange={this.handleSortChange}
                />
                <Button
                  icon={
                    sortOrder === "desc" ? "sort amount down" : "sort amount up"
                  }
                  content={sortOrder === "desc" ? "Descending" : "Ascending"}
                  onClick={this.handleSortOrderToggle}
                  size="small"
                  style={{
                    marginTop: "5px",
                    width: "100%",
                    backgroundColor: "#f4b952",
                    color: "black",
                  }}
                />
              </Grid.Column>

              <Grid.Column width={showStudentFilters ? 3 : 3}>
                <Header as="h4">Export Options</Header>
                <Button.Group vertical fluid>
                  <Button
                    icon="file excel"
                    content="Export CSV"
                    onClick={this.exportToCSV}
                    loading={isGenerating}
                    style={{
                      marginTop: "5px",
                      backgroundColor: "#225745",
                      color: "white",
                    }}
                  />
                  <Button
                    icon="file pdf"
                    content="Export PDF"
                    onClick={this.exportToPDF}
                    loading={isGenerating}
                    style={{
                      marginTop: "5px",
                      backgroundColor: "#225745",
                      color: "white",
                    }}
                  />
                </Button.Group>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        <Segment>
          <Header as="h3" dividing style={{ color: "#225745" }}>
            <Icon name="chart pie" />
            Overall Statistics
          </Header>
          <Statistic.Group widths="three" size="small">
            <Statistic color="red">
              <Statistic.Value>{totals.totalLoans}</Statistic.Value>
              <Statistic.Label>Total Loans</Statistic.Label>
            </Statistic>
            <Statistic color="blue">
              <Statistic.Value>{totals.totalUniqueBooks}</Statistic.Value>
              <Statistic.Label>Loaned Books</Statistic.Label>
            </Statistic>
            <Statistic color="green">
              <Statistic.Value>{totals.totalUniquePatrons}</Statistic.Value>
              <Statistic.Label>Total Patrons</Statistic.Label>
            </Statistic>
          </Statistic.Group>
        </Segment>

        {/* Enhanced Role Analysis Table */}
        <Segment>
          <Header as="h3" dividing style={{ color: "#225745" }}>
            <Icon name="users" />
            Analysis by User Role
          </Header>

          {filteredRoles.length === 0 ? (
            <Message warning>
              <Message.Header>No Results Found</Message.Header>
              <p>
                No data matches your current filter criteria. Try adjusting your
                filters.
              </p>
            </Message>
          ) : (
            <Table celled striped sortable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={1} textAlign="center">
                    Rank
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} textAlign="center">
                    User Role
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} textAlign="center">
                    Total Loans
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} textAlign="center">
                    Loaned Books
                  </Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="center">
                    Patrons
                  </Table.HeaderCell>
                  {showDemographicsColumn && (
                    <Table.HeaderCell width={8} textAlign="center">
                      Student Academic Breakdown
                    </Table.HeaderCell>
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredRoles.map((role, index) => (
                  <Table.Row key={role.role}>
                    <Table.Cell textAlign="center">
                      <Label circular color={index < 3 ? "green" : "blue"}>
                        {index + 1}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Header as="h4" style={{ margin: 0 }}>
                        {this.toSentenceCase(role.role)}
                      </Header>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <span>{role.totalLoans}</span>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <span>{role.uniqueBooksCount}</span>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <span>{role.uniquePatronsCount}</span>
                    </Table.Cell>
                    {showDemographicsColumn && (
                      <Table.Cell>
                        {this.renderStudentDemographics(role) || (
                          <em
                            style={{
                              color: "#999",
                              fontSize: "0.9em",
                              fontWeight: "bold",
                            }}
                          >
                            {role.role === "student"
                              ? "No demographic data available"
                              : "N/A"}
                          </em>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Segment>

        {/* Detailed Analysis for Selected Role */}
        {selectedRole && filteredRoles.length > 0 && (
          <Segment>
            <Header as="h3" dividing>
              <Icon name="zoom in" />
              Detailed Analysis: {this.toSentenceCase(selectedRole)}
            </Header>

            {filteredRoles.map((role) => {
              const topBooks = Object.values(role.books)
                .sort((a, b) => b.totalLoans - a.totalLoans)
                .slice(0, 10);

              return (
                <div key={role.role}>
                  <Header as="h4" color="blue">
                    Top 10 Most Borrowed Books by{" "}
                    {this.toSentenceCase(role.role)}
                    {role.role === "student" &&
                      (selectedProgram || selectedYearLevel) && (
                        <span style={{ marginLeft: "10px" }}>
                          {selectedProgram && (
                            <Label size="small" color="blue">
                              {
                                programOptions.find(
                                  (p) => p.value === selectedProgram
                                )?.text
                              }
                            </Label>
                          )}
                          {selectedYearLevel && (
                            <Label
                              size="small"
                              color="green"
                              style={{ marginLeft: "5px" }}
                            >
                              {
                                yearLevelOptions.find(
                                  (y) => y.value === selectedYearLevel
                                )?.text
                              }
                            </Label>
                          )}
                        </span>
                      )}
                  </Header>

                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={1}>Rank</Table.HeaderCell>
                        <Table.HeaderCell width={6}>
                          Book Title
                        </Table.HeaderCell>
                        <Table.HeaderCell width={4}>Authors</Table.HeaderCell>
                        <Table.HeaderCell width={2} textAlign="center">
                          Loans
                        </Table.HeaderCell>
                        <Table.HeaderCell width={2} textAlign="center">
                          Patrons
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {topBooks.map((book, index) => (
                        <Table.Row key={index}>
                          <Table.Cell textAlign="center">
                            <Label
                              circular
                              color={index < 3 ? "yellow" : "grey"}
                            >
                              {index + 1}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>
                            <strong>{book.title}</strong>
                          </Table.Cell>
                          <Table.Cell>
                            {book.authors.join(", ") || "Unknown"}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Label color="red">{book.totalLoans}</Label>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Label color="blue">
                              {book.uniquePatrons.size}
                            </Label>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>

                  {/* Enhanced Student Demographics Summary */}
                  {role.role === "student" &&
                    role.studentDemographics &&
                    Object.keys(role.studentDemographics).length > 0 && (
                      <div style={{ marginTop: "20px" }}>
                        <Header as="h5" color="teal">
                          <Icon name="chart bar" />
                          Detailed Student Academic Breakdown
                        </Header>

                        <Grid columns={1}>
                          <Grid.Column>
                            <Statistic.Group size="mini" widths="two">
                              <Statistic color="blue">
                                <Statistic.Value>
                                  {role.uniquePatronsCount}
                                </Statistic.Value>
                                <Statistic.Label>
                                  {selectedProgram && selectedYearLevel
                                    ? "Students"
                                    : selectedProgram
                                    ? "Students in Program"
                                    : selectedYearLevel
                                    ? "Students in Year Level"
                                    : "Total Students"}
                                </Statistic.Label>
                              </Statistic>
                              <Statistic color="green">
                                <Statistic.Value>
                                  {role.totalLoans > 0
                                    ? Math.round(
                                        role.totalLoans /
                                          role.uniquePatronsCount
                                      )
                                    : 0}
                                </Statistic.Value>
                                <Statistic.Label>
                                  Avg. Loans per Student
                                </Statistic.Label>
                              </Statistic>
                            </Statistic.Group>
                          </Grid.Column>

                          {/* <Grid.Column>
                            <Header as="h6" color="grey">
                              Academic Breakhjhjdown:
                            </Header>
                            <Table basic="very" compact size="small">
                              <Table.Header>
                                <Table.Row>
                                  <Table.HeaderCell>
                                    Program - Year
                                  </Table.HeaderCell>
                                  <Table.HeaderCell textAlign="center">
                                    Students per Program
                                  </Table.HeaderCell>
                                  <Table.HeaderCell textAlign="center">
                                    Loans per Program
                                  </Table.HeaderCell>
                                </Table.Row>
                              </Table.Header>
                              <Table.Body>
                                {Object.values(role.studentDemographics)
                                  .sort(
                                    (a, b) =>
                                      b.uniquePatronsCount -
                                      a.uniquePatronsCount
                                  )
                                  .map((data, index) => (
                                    <Table.Row key={index}>
                                      <Table.Cell>
                                        <strong>
                                          {data.program} - {data.yearLevel}
                                        </strong>
                                      </Table.Cell>
                                      <Table.Cell textAlign="center">
                                        <span>{data.uniquePatronsCount}</span>
                                      </Table.Cell>
                                      <Table.Cell textAlign="center">
                                        <span>{data.totalLoans}</span>
                                      </Table.Cell>
                                    </Table.Row>
                                  ))}
                              </Table.Body>
                            </Table>
                          </Grid.Column> */}
                        </Grid>
                      </div>
                    )}
                </div>
              );
            })}
          </Segment>
        )}

        <Divider />
      </div>
    );
  }
}

export default RoleBasedBorrowingReport;
